import React from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { useSelector } from 'react-redux';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { isInstitutionOfficerOnly } from '../../../../../../global/helpers/auth-helpers';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import { ParticipantDisplayGroup } from '../../../../../annual-enrollment/components/read-only/annual-enrollment-participants-display-group';
import { ChangeLabel, ParticipantDisplay, SelfText } from '../../../../../annual-enrollment/models/annual-enrollment-models';
import { AnnualEnrollmentProps, NewHireProps } from '../../../../models/arc-epi-props';
import { WhoWillEnrollCriterias, WhoWillEnrollOptions } from '../../models/who-will-enroll-models';
import { getRelationshipLabel } from '../helpers/coverage-display-helpers';
import { CoverageFormModel, CoveragePerson, CurrentPlanSelections } from '../models/coverage-models';


interface Props {
	settingsNewHire: NewHireProps;
	settings: AnnualEnrollmentProps;
	formValues: CoverageFormModel;
	dependents: CoveragePerson[];
	editLink: JSX.Element;
	hasMedical: boolean;
	hasDental: boolean;
	medicalPlansDisplayedDioApprover?: boolean;
	dentalPlansDisplayedDioApprover?: boolean;
	currentMedicalSelections?: CurrentPlanSelections;
	currentDentalSelections?: CurrentPlanSelections;
	isMemberFlow?: boolean;
}
export function ParticipantReview(props: Props) {

	const { settings, formValues, dependents, settingsNewHire, editLink, hasMedical, hasDental, medicalPlansDisplayedDioApprover, dentalPlansDisplayedDioApprover, currentMedicalSelections, currentDentalSelections, isMemberFlow } = props;

    const whoWillEnroll = useSelector<TcsmStates, WhoWillEnrollCriterias>(state => state?.newHireEnrollmentState.whoWillEnrollCriteria);
	const institutionOfficerOnly = isInstitutionOfficerOnly();

	const getStatusForDependent = (isSelected: boolean, guid: string, currentSelections: CurrentPlanSelections): ChangeLabel => {

		if (!currentSelections) {
			return isSelected ? ChangeLabel.Added : ChangeLabel.None;
		}

		const currentlyenrolled = currentSelections?.dependents?.includes(guid);
		if (currentlyenrolled && !isSelected) {
			return ChangeLabel.Removed;
		}

		if (!currentlyenrolled && isSelected) {
			return ChangeLabel.Added;
		}

		return ChangeLabel.None;
	};

	const getParticipantsInfo = (hasPlan: boolean, selected: string[], currentSelections: CurrentPlanSelections): ParticipantDisplay[] => {
		const participantDisplay = [];

		let selfChangeState = ChangeLabel.None;
		if (!currentSelections) {
			selfChangeState = selected?.includes(SelfText) ? ChangeLabel.Added : ChangeLabel.None;
		} else if (hasPlan && !selected?.includes(SelfText)) {
			selfChangeState = ChangeLabel.Removed;
		} else if (!hasPlan && selected?.includes(SelfText)) {
			selfChangeState = ChangeLabel.Added;
		}

		participantDisplay.push(
			{
				name: settings?.selfOptionText,
				currentlyEnrolled: currentSelections != null,
				changeLabel: selfChangeState,
				relationshipLabel: '',
			},
		);


		dependents?.map(d =>
			participantDisplay.push(
			{
				name: `${d.first_name} ${d.middle_name} ${d.last_name}`,
				changeLabel: getStatusForDependent(selected?.includes(d?.guid), d?.guid, currentSelections),
				currentlyEnrolled: currentSelections ? currentSelections?.dependents?.includes(d?.guid) : false,
				relationshipLabel: getRelationshipLabel(d?.relationship, settingsNewHire),
			}),
		);


		return participantDisplay;
	};

	const medicalParticipantsDisplayInfo = getParticipantsInfo(hasMedical, formValues.medicalDependents, currentMedicalSelections);
	const dentalParticipantsDisplayInfo = getParticipantsInfo(hasDental, formValues.dentalDependents, currentDentalSelections);

	function RenderInstitutionAdminWarningMessage() {
        if (institutionOfficerOnly && !isMemberFlow) {
            return (
                <ContentSpacer>
                    <Banner
                        banner={{
                            type: 'warning',
                            label: <WysiwygWithReactLink content={settingsNewHire?.institutionAdminPlanSelectionWarningMessage} />,
                        }}
                    />
                </ContentSpacer>
            );
        }
        return null;
    }

    if (!hasMedical && !hasDental) {
        return null;
    }

    if (whoWillEnroll?.medical === WhoWillEnrollOptions.member && whoWillEnroll?.dental === WhoWillEnrollOptions.member) {
        return null;
    }

    const shouldShowMedicalParticiapnts = whoWillEnroll?.medical !== WhoWillEnrollOptions.member && hasMedical && medicalPlansDisplayedDioApprover;
    const shouldShowDentalParticipants = whoWillEnroll?.dental !== WhoWillEnrollOptions.member && hasDental && dentalPlansDisplayedDioApprover;

    if (!shouldShowMedicalParticiapnts && !shouldShowDentalParticipants) {
        return null;
    }

    return (
        <DividerSection sectionText={settings?.participantSelectionLabel} secondarySectionNode={editLink}>
            {
                RenderInstitutionAdminWarningMessage()
            }
			{
                shouldShowMedicalParticiapnts &&
                <ParticipantDisplayGroup
                    items={medicalParticipantsDisplayInfo}
                    label={settings?.medicalCoverageLabel}
                    annualEnrollmentProps={settings}
                />
            }
            {
                shouldShowDentalParticipants &&
                <ParticipantDisplayGroup
                    items={dentalParticipantsDisplayInfo}
                    label={settings?.dentalCoverageLabel}
                    annualEnrollmentProps={settings}
                />
            }

        </DividerSection>
    );
}


ParticipantReview.defaultProps = {
	medicalPlansDisplayedDioApprover: true,
	dentalPlansDisplayedDioApprover: true,
	currentMedicalSelections: null,
	currentDentalSelections: null,
	isMemberFlow: false,
};